/**
 * domain Project -> MSPDI (Microsoft Project XML) 匯出。
 *
 * 產生的 XML 帶有 `http://schemas.microsoft.com/project` namespace，
 * 元素順序依 MSPDI xsd:sequence 慣例排列（Task/Resource/Assignment/Project
 * 各層皆有固定順序要求，因此以下所有 builder 都刻意用固定 key 插入順序）。
 */

import {
  rawNumber,
  rawString,
  type CalendarId,
  type ResourceId,
  type TaskId,
} from '../../domain/branded.js';
import type { Project } from '../../domain/project.js';
import type { Task } from '../../domain/task.js';
import type { Dependency } from '../../domain/dependency.js';
import type { Calendar, CalendarException, WorkingTime } from '../../domain/calendar.js';
import type { Resource } from '../../domain/resource.js';
import type { Assignment } from '../../domain/assignment.js';
import type { Baseline } from '../../domain/baseline.js';
import type { TimephasedCostSlice, TimephasedWorkSlice } from '../../domain/timephased.js';

import { formatIsoDuration } from './duration.js';
import { formatMspdiDate, formatMspdiDateTime } from './datetime.js';
import {
  MSPDI_BY_ACCRUAL_TYPE,
  MSPDI_BY_CONSTRAINT_TYPE,
  MSPDI_BY_DEPENDENCY_TYPE,
  MSPDI_BY_RESOURCE_TYPE,
  MSPDI_BY_TASK_TYPE,
  MSPDI_DAY_TYPE_BY_WEEKDAY_INDEX,
  MSPDI_EXCEPTION_DAY_TYPE,
  MSPDI_LAG_FORMAT_ELAPSED,
  MSPDI_LAG_FORMAT_ELAPSED_PERCENT,
  MSPDI_LAG_FORMAT_PERCENT,
  MSPDI_LAG_FORMAT_WORKING,
} from './constants.js';
import { createMspdiBuilder, type XmlNode } from './xml.js';

const MSPDI_NAMESPACE = 'http://schemas.microsoft.com/project';

export function exportMspdi(project: Project): string {
  const calendarUidOf = buildCalendarUidMap(project.calendars);
  const assignmentUidOf = buildAssignmentUidMap(project.assignments);
  const taskUidOf = buildTaskUidMap(project.tasks);
  const resourceUidOf = buildResourceUidMap(project.resources);

  const dependenciesByTaskId = new Map<string, Dependency[]>();
  for (const dependency of project.dependencies) {
    const list = dependenciesByTaskId.get(rawString(dependency.successorId)) ?? [];
    list.push(dependency);
    dependenciesByTaskId.set(rawString(dependency.successorId), list);
  }

  const baselinesByTaskId = new Map<string, Baseline[]>();
  const baselinesByAssignmentId = new Map<string, Baseline[]>();
  for (const baseline of project.baselines) {
    for (const snapshot of baseline.tasks) {
      const key = rawString(snapshot.taskId);
      const list = baselinesByTaskId.get(key) ?? [];
      list.push(baseline);
      baselinesByTaskId.set(key, list);
    }
    for (const snapshot of baseline.assignments) {
      const key = rawString(snapshot.assignmentId);
      const list = baselinesByAssignmentId.get(key) ?? [];
      list.push(baseline);
      baselinesByAssignmentId.set(key, list);
    }
  }

  const projectNode: XmlNode = {
    Name: project.name,
    ScheduleFromStart: project.scheduleFrom === 'ProjectStart' ? '1' : '0',
    StartDate: formatMspdiDateTime(project.startDate),
    FinishDate: formatMspdiDateTime(project.finishDate),
    CurrencySymbol: project.currency,
    CalendarUID: String(calendarUidOf.get(project.defaultCalendarId) ?? 1),
    MinutesPerDay: String(
      project.options ? rawNumber(project.options.durationConversion.minutesPerDay) : 480,
    ),
    MinutesPerWeek: String(
      project.options ? rawNumber(project.options.durationConversion.minutesPerWeek) : 2400,
    ),
    DaysPerMonth: String(project.options?.durationConversion.daysPerMonth ?? 20),
    ...(project.statusDate ? { StatusDate: formatMspdiDateTime(project.statusDate) } : {}),
    Calendars: {
      Calendar: project.calendars.map((c) => calendarToXml(c, calendarUidOf)),
    },
    Tasks: {
      Task: project.tasks.map((t) =>
        taskToXml(
          t,
          calendarUidOf,
          taskUidOf,
          dependenciesByTaskId.get(rawString(t.id)) ?? [],
          baselinesByTaskId.get(rawString(t.id)) ?? [],
        ),
      ),
    },
    Resources: {
      Resource: project.resources.map((r) => resourceToXml(r, calendarUidOf)),
    },
    Assignments: {
      Assignment: [...project.assignments.map((a) =>
        assignmentToXml(a, assignmentUidOf, taskUidOf, resourceUidOf, baselinesByAssignmentId.get(rawString(a.id)) ?? []),
      ), ...budgetAssignmentsToXml(project, resourceUidOf, project.assignments.length + 1, project.baselines)],
    },
  };

  const document = {
    '?xml': { '@_version': '1.0', '@_encoding': 'UTF-8', '@_standalone': 'yes' },
    Project: {
      '@_xmlns': MSPDI_NAMESPACE,
      ...projectNode,
    },
  };

  const builder = createMspdiBuilder();
  return builder.build(document) as string;
}

// ---------------------------------------------------------------------------
// UID 對照表
// ---------------------------------------------------------------------------

function buildCalendarUidMap(calendars: readonly Calendar[]): Map<CalendarId, number> {
  const map = new Map<CalendarId, number>();
  calendars.forEach((c, index) => map.set(c.id, index + 1));
  return map;
}

function buildAssignmentUidMap(assignments: readonly Assignment[]): Map<string, number> {
  const map = new Map<string, number>();
  assignments.forEach((a, index) => map.set(rawString(a.id), index + 1));
  return map;
}

function buildTaskUidMap(tasks: readonly Task[]): Map<TaskId, number> {
  const map = new Map<TaskId, number>();
  tasks.forEach((t) => map.set(t.id, t.uid));
  return map;
}

function buildResourceUidMap(resources: readonly Resource[]): Map<ResourceId, number> {
  const map = new Map<ResourceId, number>();
  resources.forEach((r) => map.set(r.id, r.uid));
  return map;
}

// ---------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------

function calendarToXml(calendar: Calendar, calendarUidOf: Map<CalendarId, number>): XmlNode {
  const uid = calendarUidOf.get(calendar.id) ?? 1;
  const weekDayEntries: XmlNode[] = [];

  calendar.weekDays.forEach((spec, index) => {
    // `null`（未指定，往 base calendar 繼承）在 MSPDI 沒有對應語意，匯出時略過，
    // 交由 BaseCalendarUID 繼承鏈處理（MS Project 開啟時該日將沿用基礎日曆）。
    if (spec === null) {
      return;
    }
    weekDayEntries.push({
      DayType: String(MSPDI_DAY_TYPE_BY_WEEKDAY_INDEX[index]),
      DayWorking: spec.working ? '1' : '0',
      ...(spec.working ? { WorkingTimes: { WorkingTime: spec.times.map(workingTimeToXml) } } : {}),
    });
  });

  for (const exception of calendar.exceptions) {
    weekDayEntries.push(exceptionToXml(exception));
  }

  return {
    UID: String(uid),
    Name: calendar.name,
    IsBaseCalendar: calendar.baseCalendarId === null ? '1' : '0',
    ...(calendar.baseCalendarId !== null
      ? { BaseCalendarUID: String(calendarUidOf.get(calendar.baseCalendarId) ?? 1) }
      : {}),
    WeekDays: { WeekDay: weekDayEntries },
  };
}

function workingTimeToXml(time: WorkingTime): XmlNode {
  return {
    FromTime: minutesToClock(rawNumber(time.start)),
    ToTime: minutesToClock(rawNumber(time.end)),
  };
}

function minutesToClock(totalMinutes: number): string {
  const hh = Math.floor(totalMinutes / 60) % 24;
  const mm = totalMinutes % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`;
}

function exceptionToXml(exception: CalendarException): XmlNode {
  return {
    Name: exception.name,
    DayType: String(MSPDI_EXCEPTION_DAY_TYPE),
    DayWorking: exception.working ? '1' : '0',
    TimePeriod: {
      FromDate: formatMspdiDate(exception.from),
      ToDate: formatMspdiDate(exception.to),
    },
    ...(exception.working && exception.workingTimes.length > 0
      ? { WorkingTimes: { WorkingTime: exception.workingTimes.map(workingTimeToXml) } }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Task
// ---------------------------------------------------------------------------

function taskToXml(
  task: Task,
  calendarUidOf: Map<CalendarId, number>,
  taskUidOf: Map<TaskId, number>,
  predecessors: readonly Dependency[],
  baselines: readonly Baseline[],
): XmlNode {
  return {
    UID: String(task.uid),
    ID: String(task.uid),
    Name: task.name,
    Manual: task.isManuallyScheduled ? '1' : '0',
    Type: String(MSPDI_BY_TASK_TYPE[task.taskType]),
    WBS: task.wbs,
    OutlineNumber: task.wbs,
    OutlineLevel: String(task.outlineLevel),
    Priority: String(task.priority),
    Start: formatMspdiDateTime(task.start),
    Finish: formatMspdiDateTime(task.finish),
    Duration: formatIsoDuration(task.durationMinutes),
    Work: formatIsoDuration(task.work),
    EffortDriven: task.effortDriven ? '1' : '0',
    Milestone: task.isMilestone ? '1' : '0',
    Summary: task.isSummary ? '1' : '0',
    Critical: task.isCritical ? '1' : '0',
    EarlyStart: formatMspdiDateTime(task.earlyStart),
    EarlyFinish: formatMspdiDateTime(task.earlyFinish),
    LateStart: formatMspdiDateTime(task.lateStart),
    LateFinish: formatMspdiDateTime(task.lateFinish),
    FreeSlack: String(Math.round(rawNumber(task.freeFloat) * 10)),
    TotalSlack: String(Math.round(rawNumber(task.totalFloat) * 10)),
    FixedCost: String(rawNumber(task.fixedCost)),
    FixedCostAccrual: String(MSPDI_BY_ACCRUAL_TYPE[task.fixedCostAccrual]),
    PercentComplete: String(rawNumber(task.percentComplete)),
    PercentWorkComplete: String(rawNumber(task.percentWorkComplete)),
    ConstraintType: String(MSPDI_BY_CONSTRAINT_TYPE[task.constraintType]),
    IgnoreResourceCalendar: task.ignoreResourceCalendar ? '1' : '0',
    ...(task.calendarId !== null ? { CalendarUID: String(calendarUidOf.get(task.calendarId) ?? 1) } : {}),
    ...(task.constraintDate ? { ConstraintDate: formatMspdiDateTime(task.constraintDate) } : {}),
    ...(task.deadline ? { Deadline: formatMspdiDateTime(task.deadline) } : {}),
    ...(task.actualStart ? { ActualStart: formatMspdiDateTime(task.actualStart) } : {}),
    ...(task.actualFinish ? { ActualFinish: formatMspdiDateTime(task.actualFinish) } : {}),
    Notes: task.notes,
    ...(predecessors.length > 0
      ? { PredecessorLink: predecessors.map((d) => predecessorLinkToXml(d, taskUidOf)) }
      : {}),
    ...(baselines.length > 0
      ? { Baseline: baselines.map((b) => taskBaselineToXml(b, task.id)) }
      : {}),
  };
}

function predecessorLinkToXml(dependency: Dependency, taskUidOf: Map<TaskId, number>): XmlNode {
  const predecessorTaskUid = taskUidOf.get(dependency.predecessorId) ?? 0;
  const isPercent = dependency.lagIsPercent === true;
  const isElapsed = dependency.lagIsElapsed === true;
  const lagRaw = isPercent
    ? Math.round(rawNumber(dependency.lagMinutes) / 10)
    : Math.round(rawNumber(dependency.lagMinutes) * 10);
  // 只保留「一般 vs. elapsed」與「一般 vs. 百分比」兩個二元語意（domain 的
  // lagIsElapsed/lagIsPercent），不追蹤原始顯示單位（m/h/d/w/mo 只是同一個
  // 分鐘數的不同顯示格式，彼此等價）。用固定的代表性 LagFormat 匯出，
  // 確保 import -> export -> import 後 lagIsElapsed/lagIsPercent/lagMinutes
  // 三者都精確不變——這才是需要保證的 round-trip 不變量。
  const lagFormat = isPercent
    ? isElapsed
      ? MSPDI_LAG_FORMAT_ELAPSED_PERCENT
      : MSPDI_LAG_FORMAT_PERCENT
    : isElapsed
      ? MSPDI_LAG_FORMAT_ELAPSED
      : MSPDI_LAG_FORMAT_WORKING;
  return {
    PredecessorUID: String(predecessorTaskUid),
    Type: String(MSPDI_BY_DEPENDENCY_TYPE[dependency.type]),
    LinkLag: String(lagRaw),
    LagFormat: String(lagFormat),
  };
}

function taskBaselineToXml(baseline: Baseline, taskId: Task['id']): XmlNode {
  const snapshot = baseline.tasks.find((t) => t.taskId === taskId);
  if (!snapshot) {
    return { '@_Number': String(baseline.number) };
  }
  return {
    '@_Number': String(baseline.number),
    Start: formatMspdiDateTime(snapshot.start),
    Finish: formatMspdiDateTime(snapshot.finish),
    Duration: formatIsoDuration(snapshot.durationMinutes),
    Work: formatIsoDuration(snapshot.work),
    Cost: String(rawNumber(snapshot.cost)),
    FixedCost: String(rawNumber(snapshot.fixedCost)),
  };
}

// ---------------------------------------------------------------------------
// Resource
// ---------------------------------------------------------------------------

function resourceToXml(resource: Resource, calendarUidOf: Map<CalendarId, number>): XmlNode {
  return {
    UID: String(resource.uid),
    Name: resource.name,
    Type: String(MSPDI_BY_RESOURCE_TYPE[resource.type]),
    ...(resource.isBudget ? { IsBudget: '1' } : {}),
    Initials: resource.initials,
    ...(resource.materialLabel ? { MaterialLabel: resource.materialLabel } : {}),
    Group: resource.group,
    MaxUnits: String(rawNumber(resource.maxUnits)),
    AccrueAt: String(MSPDI_BY_ACCRUAL_TYPE[resource.accrueAt]),
    StandardRate: String(rawNumber(resource.standardRate) * 60),
    StandardRateFormat: '2',
    OvertimeRate: String(rawNumber(resource.overtimeRate) * 60),
    OvertimeRateFormat: '2',
    CostPerUse: String(rawNumber(resource.costPerUse)),
    ...(resource.rateTables ? { Rates: { Rate: rateTablesToXml(resource) } } : {}),
    ...(resource.calendarId !== null ? { CalendarUID: String(calendarUidOf.get(resource.calendarId) ?? 1) } : {}),
    ...(resource.notes ? { Notes: resource.notes } : {}),
    ...(resource.availability.length > 0
      ? {
          AvailabilityPeriods: {
            AvailabilityPeriod: resource.availability.map((a) => ({
              AvailableFrom: formatMspdiDate(a.from),
              ...(a.to ? { AvailableTo: formatMspdiDate(a.to) } : {}),
              AvailableUnits: String(rawNumber(a.maxUnits)),
            })),
          },
        }
      : {}),
  };
}

function budgetAssignmentsToXml(project: Project, resourceUidOf: Map<ResourceId, number>, firstUid: number, baselines: readonly Baseline[]): XmlNode[] {
  return (project.budgetAssignments ?? []).map((assignment, index) => ({
    UID: String(firstUid + index), TaskUID: '0', ResourceUID: String(resourceUidOf.get(assignment.resourceId) ?? 0),
    ...(assignment.budgetWork !== undefined ? { BudgetWork: formatIsoDuration(assignment.budgetWork) } : {}),
    ...(assignment.budgetCost !== undefined ? { BudgetCost: String(rawNumber(assignment.budgetCost)) } : {}),
    ...(baselines.length > 0 ? { Baseline: baselines.map((baseline) => budgetBaselineToXml(baseline, assignment.id)) } : {}),
  }));
}
function budgetBaselineToXml(baseline: Baseline, assignmentId: string): XmlNode {
  const snapshot = baseline.budgetAssignments?.find((item) => item.budgetAssignmentId === assignmentId);
  return { '@_Number': String(baseline.number), ...(snapshot?.budgetWork !== undefined ? { BudgetWork: formatIsoDuration(snapshot.budgetWork) } : {}), ...(snapshot?.budgetCost !== undefined ? { BudgetCost: String(rawNumber(snapshot.budgetCost)) } : {}) };
}

function rateTablesToXml(resource: Resource): XmlNode[] {
  const tableCodes = { A: 0, B: 1, C: 2, D: 3, E: 4 } as const;
  const rows: XmlNode[] = [];
  for (const table of ['A', 'B', 'C', 'D', 'E'] as const) {
    const tableRows = resource.rateTables?.[table] ?? [];
    for (let index = 0; index < tableRows.length; index += 1) {
      const rate = tableRows[index]!;
      const next = tableRows[index + 1];
      rows.push({
      RatesFrom: formatMspdiDateTime(rate.effectiveFrom), RatesTo: next ? formatMspdiDateTime(next.effectiveFrom) : '2099-12-31T23:59:59', RateTable: String(tableCodes[table]),
      StandardRate: String(rawNumber(rate.standardRate) * 60), StandardRateFormat: '2',
      OvertimeRate: String(rawNumber(rate.overtimeRate) * 60), OvertimeRateFormat: '2', CostPerUse: String(rawNumber(rate.costPerUse)),
    });
    }
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Assignment
// ---------------------------------------------------------------------------

function assignmentToXml(
  assignment: Assignment,
  assignmentUidOf: Map<string, number>,
  taskUidOf: Map<TaskId, number>,
  resourceUidOf: Map<ResourceId, number>,
  baselines: readonly Baseline[],
): XmlNode {
  const uid = assignmentUidOf.get(rawString(assignment.id)) ?? 1;
  const taskUid = taskUidOf.get(assignment.taskId) ?? 0;
  const resourceUid = resourceUidOf.get(assignment.resourceId) ?? 0;

  return {
    UID: String(uid),
    TaskUID: String(taskUid),
    ResourceUID: String(resourceUid),
    ...(assignment.rateTable && assignment.rateTable !== 'A' ? { RateTable: String({ A: 0, B: 1, C: 2, D: 3, E: 4 }[assignment.rateTable]) } : {}),
    ActualCost: String(rawNumber(assignment.actualCost)),
    ActualWork: formatIsoDuration(assignment.actualWork),
    Cost: String(rawNumber(assignment.cost)),
    Finish: formatMspdiDateTime(assignment.finish),
    ...(assignment.notes ? { Notes: assignment.notes } : {}),
    RemainingWork: formatIsoDuration(assignment.remainingWork),
    Start: formatMspdiDateTime(assignment.start),
    Units: String(rawNumber(assignment.units)),
    Work: formatIsoDuration(assignment.work),
    ...(baselines.length > 0
      ? { Baseline: baselines.map((b) => assignmentBaselineToXml(b, assignment.id)) }
      : {}),
    ...(assignment.timephased ? { TimephasedData: timephasedToXml(assignment.timephased) } : {}),
  };
}

function assignmentBaselineToXml(baseline: Baseline, assignmentId: Assignment['id']): XmlNode {
  const snapshot = baseline.assignments.find((a) => a.assignmentId === assignmentId);
  if (!snapshot) {
    return { '@_Number': String(baseline.number) };
  }
  return {
    '@_Number': String(baseline.number),
    Start: formatMspdiDateTime(snapshot.start),
    Finish: formatMspdiDateTime(snapshot.finish),
    Work: formatIsoDuration(snapshot.work),
    Cost: String(rawNumber(snapshot.cost)),
  };
}

function timephasedToXml(set: NonNullable<Assignment['timephased']>): XmlNode[] {
  const entries: XmlNode[] = [];
  const pushWork = (slices: readonly TimephasedWorkSlice[], type: number) => {
    for (const slice of slices) {
      entries.push({
        Type: String(type),
        Start: formatMspdiDateTime(slice.start),
        Finish: formatMspdiDateTime(slice.end),
        Value: formatIsoDuration(slice.valueMinutes),
      });
    }
  };
  const pushCost = (slices: readonly TimephasedCostSlice[], type: number) => {
    for (const slice of slices) {
      entries.push({
        Type: String(type),
        Start: formatMspdiDateTime(slice.start),
        Finish: formatMspdiDateTime(slice.end),
        Value: String(rawNumber(slice.valueCost)),
      });
    }
  };
  pushWork(set.work, 0);
  pushWork(set.remainingWork, 1);
  pushWork(set.actualWork, 2);
  if (set.overtimeWork) {
    pushWork(set.overtimeWork, 3);
  }
  if (set.baselineWork) {
    pushWork(set.baselineWork, 4);
  }
  if (set.baselineCost) {
    pushCost(set.baselineCost, 5);
  }
  pushCost(set.actualCost, 6);
  return entries;
}
